<template>
  <Modal id="cart" :show-overlay="true" :on-open="onOpen" :on-close="onClose">
    <template v-slot="{isOpen, toggle, open, close}">
      <slot :is-open="isOpen" :toggle="toggle" :open="open" :close="close" />
    </template>
    <template v-slot:content="{isOpen, close}">
      <Transition name="slide-from-right">
        <div
          v-show="isOpen"
          class="fixed bottom-0 right-0 top-0 z-50 flex h-full w-full flex-col overflow-y-auto overflow-x-hidden sm:border-l-2 border-blue bg-white text-blue shadow-2xl md:w-[420px]"
        >
          <div id="sr-cart-alert" class="sr-only" aria-live="polite" />

          <div
            v-if="cartAnnouncementEnabled && cartAnnouncements.length"
            class="bg-green text-body-sm max-sm:text-xs font-medium text-center px-20 py-[7.5px]"
          >
            <template v-if="cartAnnouncements.length === 1">
              <a
                v-if="cartAnnouncements[0].url"
                :href="cartAnnouncements[0].url"
                class="block"
                v-html="cartAnnouncements[0].text"
              />
              <div v-else class="block" v-html="cartAnnouncements[0].text" />
            </template>
            <CarouselKeen
              v-else
              :slides="1"
              :tablet_slides="1"
              :mobile_slides="1"
              :arrows="false"
              :progressbar="false"
              :loop="true"
              :autoplay="5000"
            >
              <div
                v-for="(item, idx) in cartAnnouncements"
                :key="`cart-ab-${idx}`"
                class="keen-slider__slide flex items-center justify-center"
              >
                <a
                  v-if="item.url"
                  :href="item.url"
                  class="block w-full"
                  v-html="item.text"
                />
                <div v-else class="block w-full" v-html="item.text" />
              </div>
            </CarouselKeen>
          </div>

          <div
            v-if="freeShippingEnabled && items.length"
            class="px-20 py-10"
          >
            <p
              class="mb-5 text-center text-xs font-medium text-blue"
              v-html="freeShippingMessage"
            />
            <div class="relative h-[8px] w-full border border-green bg-white">
              <div
                class="h-full bg-green transition-[width] duration-300"
                :style="freeShippingStyle"
              />
            </div>
          </div>

          <div class="relative border-b border-blue px-20 py-[6px]">
            <button
              class="absolute right-15 top-15 flex h-30 w-30 items-center justify-center text-blue hover:text-green"
              @click="close"
              aria-label="Close cart"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="1"
                  y1="1"
                  x2="21"
                  y2="21"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <line
                  x1="21"
                  y1="1"
                  x2="1"
                  y2="21"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </button>
            <h3 class="text-h4 font-medium capitalize">Your Cart</h3>
            <p class="mt-5 text-sm font-medium text-gray">
              {{ itemCount }} {{ itemCount === 1 ? "Item" : "Items" }}
            </p>
          </div>

          <div
            class="min-h-[150px] flex-[1_1_auto] overflow-y-auto px-20 py-10"
          >
            <cart-item
          v-for="item in items"
          :key="item.viewKey || item.key"
          :item="item"
        />

            <div
              v-if="!items.length && !loading"
              class="flex h-full flex-col py-20 text-center"
            >
              <div class="my-auto">
                <span class="mb-10 block text-h5 font-medium text-blue">
                  Looks like your cart is empty!
                </span>
                <span class="block text-sm text-gray">
                  {{ empty_cart_label }}
                </span>
              </div>

              <a
                href="/collections/all"
                class="mt-20 button w-full max-w-full flex-[0_0_50px]"
              >
                Shop Now
              </a>

              <a
                v-if="empty_cart_btn1_link"
                :href="empty_cart_btn1_link"
                class="mt-10 button w-full max-w-full flex-[0_0_50px]"
              >
                {{ empty_cart_btn1_label || "Shop Best Sellers" }}
              </a>

              <a
                v-if="empty_cart_btn2_link"
                :href="empty_cart_btn2_link"
                class="mt-10 button  w-full max-w-full flex-[0_0_50px]"
              >
                {{ empty_cart_btn2_label || "Shop New Arrivals" }}
              </a>
            </div>
          </div>

          <div
            v-if="showUpsaleEnabled && computedUpsaleProducts.length"
            class="px-20"
          >
            <div
              class="py-15"
              :class="{'border-t  border-blue': items.length}"
            >
              <h4
                class="font-medium capitalize text-blue mb-5"
               
              >
                {{
                  items.length
                    ? upsale_headline
                    : upsale_headline_best_sellers
                }}
              </h4>

              <component
                :is="items.length !== 0 ? 'CarouselKeen' : 'div'"
                v-bind="
                  items.length !== 0
                    ? {
                        slides: 1,
                        tablet_slides: 1,
                        mobile_slides: 1,
                        arrows: true,
                        arrows_side: true,
                        progressbar: false,
                      }
                    : {}
                "
                :key="`${items.length}-${visible}`"
                class="w-full"
                :class="[items.length !== 0 ? 'px-25' : '']"
              >
                <div
                  v-for="(product, idx) in computedUpsaleProducts"
                  :key="`upsell-${idx}`"
                  class="keen-slider__slide"
                >
                  <cart-item
                    :item="product"
                    :show-add-to-cart-button="true"
                  />
                </div>
              </component>
            </div>
          </div>

          <div v-if="items.length" class="border-t border-blue px-20 py-15">
            <div class="mb-10 flex items-end justify-between text-sm">
              <span class="font-bold uppercase">
                Subtotal ({{ itemCount }} {{ itemCount === 1 ? "Item" : "Items" }})
              </span>
              <span class="flex items-baseline gap-5">
                <del
                  v-if="subtotalCompareAt"
                  class="text-xs text-gray"
                  v-text="formatMoney(subtotalCompareAt)"
                />
                <span class="text-base font-bold text-blue">
                  {{ formatMoney(cartData.total_price) }}
                </span>
              </span>
            </div>

            <discount-code :key="items.length" />

            <a
              href="/checkout"
              class="mt-10 button max-w-full w-full"
            >
              Checkout
            </a>
          </div>
        </div>
      </Transition>
    </template>
  </Modal>
</template>

<script>
export default {
  name: "Cart",
};
</script>

<script setup>
import {triggerEvent} from "@/utils/custom-events";
import {formatMoney as themeFormatMoney} from "@shopify/theme-currency";
import {onBeforeMount, computed, watch} from "vue";
import {useStore} from "vuex";

const props = defineProps({
  empty_cart_label: {
    type: String,
    default: "Add your pet's favorite items to the cart!",
  },
  upsale_headline: {
    type: String,
    default: "You might also like",
  },
  upsale_headline_best_sellers: {
    type: String,
    default: "Best Sellers",
  },
  upsale_products: {
    type: String,
    default: "[]",
  },
  show_upsale: {
    type: [String, Boolean],
    default: false,
  },
  empty_cart_btn1_label: {
    type: String,
    default: "",
  },
  empty_cart_btn1_link: {
    type: String,
    default: "",
  },
  empty_cart_btn2_label: {
    type: String,
    default: "",
  },
  empty_cart_btn2_link: {
    type: String,
    default: "",
  },
  cart_announcement_visible: {
    type: [String, Boolean],
    default: false,
  },
  cart_announcement_content: {
    type: String,
    default: "",
  },
  free_shipping_amount: {
    type: [String, Number],
    default: 0,
  },
  free_shipping_progress_label: {
    type: String,
    default: "You are $[XX] away from free shipping!",
  },
  free_shipping_success_label: {
    type: String,
    default: "Congrats! You get free shipping.",
  },
});

const store = useStore();

const cartData = computed(() => store.state.cart.cartData);
const visible = computed(() => store.state.cart.visible);
const loading = computed(() => store.state.cart.loading);
const items = computed(() => (cartData.value ? cartData.value.items : []));
const itemCount = computed(() => cartData.value?.item_count ?? 0);

// Strike value compared against cart.total_price (final amount after every
// discount). Per-line baseline is final_line_price + total_discount, i.e. the
// pre-promo line price; we then take the higher of compare_at_price /
// original_line_price so combined line + cart discounts surface in the strike.
const subtotalCompareAt = computed(() => {
  const finalTotal = Number(cartData.value?.total_price) || 0;
  let strike = 0;
  for (const item of items.value) {
    const allocation = item.selling_plan_allocation || {};
    const qty = Number(item.quantity) || 1;
    const finalLine = Number(item.final_line_price ?? item.final_price * qty) || 0;
    const totalDiscount = Number(item.total_discount) || 0;
    let lineStrike = finalLine + totalDiscount;
    const candidates = [
      allocation.compare_at_price ? allocation.compare_at_price * qty : null,
      item.original_line_price,
      item.compare_at_price ? item.compare_at_price * qty : null,
    ];
    for (const candidate of candidates) {
      const num = Number(candidate);
      if (Number.isFinite(num) && num > lineStrike) {
        lineStrike = num;
        break;
      }
    }
    strike += lineStrike;
  }
  return strike > finalTotal ? strike : 0;
});

const upsaleProducts = (() => {
  try {
    return JSON.parse(props.upsale_products) || [];
  } catch {
    return [];
  }
})();

const computedUpsaleProducts = computed(() =>
  upsaleProducts.filter(product => {
    const existsInCart = items.value.some(
      cartItem => cartItem.handle === product.handle,
    );
    return !existsInCart && product.available !== false;
  }),
);

const showUpsaleEnabled = computed(() => {
  if (typeof props.show_upsale === "boolean") return props.show_upsale;
  return props.show_upsale === "true" || props.show_upsale === true;
});

const cartAnnouncementEnabled = computed(() => {
  if (typeof props.cart_announcement_visible === "boolean") return props.cart_announcement_visible;
  return props.cart_announcement_visible === "true" || props.cart_announcement_visible === true;
});

const cartAnnouncements = computed(() => {
  if (props.cart_announcement_content && props.cart_announcement_content.trim()) {
    return [{text: props.cart_announcement_content, url: ""}];
  }
  if (typeof window !== "undefined" && Array.isArray(window.Shopify?.announcement)) {
    return window.Shopify.announcement.filter(a => a && a.text);
  }
  return [];
});

const freeShippingThreshold = computed(() => {
  const n = Number(props.free_shipping_amount);
  return Number.isFinite(n) && n > 0 ? n : 0;
});

const freeShippingEnabled = computed(() => freeShippingThreshold.value > 0);

const freeShippingRemaining = computed(() => {
  const total = (Number(cartData.value?.total_price) || 0) / 100;
  return Math.max(freeShippingThreshold.value - total, 0);
});

const freeShippingMessage = computed(() => {
  if (freeShippingRemaining.value <= 0) {
    return props.free_shipping_success_label;
  }
  const formatted = freeShippingRemaining.value.toFixed(2);
  return props.free_shipping_progress_label.replace("[XX]", formatted);
});

const freeShippingStyle = computed(() => {
  const total = (Number(cartData.value?.total_price) || 0) / 100;
  const pct = freeShippingThreshold.value
    ? Math.min((total / freeShippingThreshold.value) * 100, 100)
    : 0;
  return `width: ${pct}%;`;
});

const formatMoney = value => themeFormatMoney(value, "${{amount}}");

const onOpen = () => {
  store.dispatch("cart/show");
};

const onClose = () => {
  store.dispatch("cart/hide");
};

watch(visible, value => {
  if (value) {
    triggerEvent("modal:cart:open");
  } else {
    triggerEvent("modal:cart:close");
  }
});

onBeforeMount(() => {
  store.dispatch("cart/initCart");
});
</script>
